import React from 'react';
import { useController, UseFormReturn } from 'react-hook-form';
import { ToolbarOptions } from '../StudyListToolbar';
import SortDisplayControls from './SortDisplayControls';
import { FilterFormContent } from './FilterFormContent';
import type { FilterSortDisplayForm } from './useFilterSortDisplayForm';
import styles from './SortAndFilterSidebarContent.module.scss';
import { Button, Icon, IconList } from 'components/core';
import { Inline, Stack } from 'components/layout';
import { SidebarBody, SidebarFooter } from 'components/shared/Sidebar';
import { useUniqueID } from 'hooks/useAccessibleName';

type Props = {
  form: UseFormReturn<FilterSortDisplayForm>;
  sortOptions: Record<string, string>;
  tags: string[];
  isAwaitingOtherReviewer: boolean;
  reviewStage: ToolbarOptions['reviewStage'];
  isResetting: boolean;
  onApply: (values: FilterSortDisplayForm) => Promise<void>;
  onReset: () => Promise<void>;
};

export const SortAndFilterSidebarContent = ({
  form,
  sortOptions,
  tags,
  isAwaitingOtherReviewer,
  reviewStage,
  isResetting,
  onApply,
  onReset,
}: Props) => {
  const formId = useUniqueID({ postfix: 'sort-and-filter-form' });
  const { isSubmitting } = form.formState;
  const sortField = useController({ control: form.control, name: 'sort' });
  const displayField = useController({
    control: form.control,
    name: 'display',
  });

  return (
    <>
      <SidebarBody>
        <form id={formId} onSubmit={form.handleSubmit(onApply)}>
          <Stack spacing={5}>
            <SortDisplayControls
              sort={sortField.field.value}
              display={displayField.field.value}
              sortOptions={sortOptions}
              onSortChange={sortField.field.onChange}
              onDisplayChange={displayField.field.onChange}
            />
            <div className={styles.SectionDivider} />
            <FilterFormContent
              tags={tags}
              form={form}
              isAwaitingOtherReviewer={isAwaitingOtherReviewer}
              reviewStage={reviewStage}
            />
          </Stack>
        </form>
      </SidebarBody>
      <SidebarFooter>
        <Inline alignX="left" spacing={3}>
          <Button
            type="brand"
            htmlType="submit"
            form={formId}
            isLoading={isSubmitting}
            disabled={isResetting}
            data-pendo-key="apply-sort-and-filter"
            className={styles.FooterButton}
          >
            Apply
          </Button>
          <Button
            type="neutral"
            variant="outline"
            htmlType="button"
            isLoading={isResetting}
            disabled={isSubmitting}
            onClick={onReset}
            data-pendo-key="reset-filters"
            className={styles.FooterButton}
          >
            <Icon icon={IconList.light.faArrowRotateLeft} />
            Reset filters
          </Button>
        </Inline>
      </SidebarFooter>
    </>
  );
};
